import { Spinner, Tag, Text, TextButton } from '@zillow/constellation-10';
import { LoadingConstants, pathsManifest } from '@zg-rentals/rental-manager-env';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { Link } from 'react-router-dom';
import type { UserState } from '../../hooks/userStates';
import type { UserManagementUserProfileDto } from '@zg-rentals/ts-user-management-client';

const getRole = ({ isAdmin, isApplicationManager }: { isAdmin: boolean; isApplicationManager: boolean }) => {
  let role = isAdmin ? 'Company admin' : 'User';
  if (isApplicationManager) {
    role += ', application manager';
  }
  return role;
};

const roleRenderer = (user: UserManagementUserProfileDto) => <div>{getRole(user)}</div>;

interface getConfigsTypes {
  isScreenMobile: boolean;
  onResendInvite: (user: UserManagementUserProfileDto) => void;
  yourToken: string;
  activeTab: string;
  companyUsers: UserState;
  previewUserProperties: (brand: string, token: string, onlyUnpaidProperties: boolean) => void;
  previewPendingProperties: (email: string, onlyUnpaidProperties: boolean) => void;
}

const columnConfigs = {
  getConfigs({
    isScreenMobile,
    onResendInvite,
    yourToken,
    activeTab,
    companyUsers,
    previewUserProperties,
    previewPendingProperties,
  }: getConfigsTypes): Array<ColumnConfig<UserManagementUserProfileDto>> {
    const paths = pathsManifest.getAllPaths('');
    const basePath = `${paths.userManagementPage}/${paths.userManagementEditUser}`;
    const toUserManagementPath = (user: UserManagementUserProfileDto) => {
      const { userToken, email, isPending } = user;
      if (isPending) {
        return `${basePath}?email=${email}`;
      }
      return `${basePath}?userBrand=zillow&userToken=${userToken}`;
    };
    const hasOlympusListings = Object.values(companyUsers.propertyData).some(
      (propertyData) => propertyData.olympusTier3ListingCount > 0,
    );
    // Renderers
    const userRenderer = (user: UserManagementUserProfileDto) => (
      <div>
        <div>
          <Text textStyle="body-sm-bold">{`${String(user.email).split('@')[0]} `}</Text>
          {user.userToken === yourToken && (
            <Tag tone="gray" size="sm">
              You
            </Tag>
          )}
          {user.isPending && (
            <Tag tone="gray" size="sm">
              Pending
            </Tag>
          )}
          {user.isPendingDelete && (
            <Tag tone="gray" size="sm">
              Pending Deactivation
            </Tag>
          )}
        </div>
        {user.email}
        {user.isPending && (
          <div>
            <TextButton onClick={() => onResendInvite(user)}>Resend invite</TextButton>
          </div>
        )}
      </div>
    );

    const actionRenderer = (user: UserManagementUserProfileDto) =>
      user.isPendingDelete ? (
        '-'
      ) : (
        <TextButton asChild>
          <Link to={toUserManagementPath(user)}>{user.isCurrentUser ? 'View' : 'Edit'}</Link>
        </TextButton>
      );

    const previewProperties = (user: UserManagementUserProfileDto, onlyUnpaidProperties = false) => {
      if (user.isPending) {
        previewPendingProperties(user.email, onlyUnpaidProperties);
      } else {
        previewUserProperties('zillow', user.userToken, onlyUnpaidProperties);
      }
    };

    const paidPropertyRenderer = (user: UserManagementUserProfileDto) => {
      if (user.isPendingDelete) {
        return '-';
      }

      const userId = user.isPending ? user.email : user.userToken;
      const propertyData = companyUsers.propertyData[userId];
      if (!propertyData) {
        return <Spinner size="sm" />;
      }
      if (propertyData.loadingState === LoadingConstants.ERROR) {
        return '-';
      }
      if (propertyData.loadingState === LoadingConstants.SUCCESS) {
        const { propertyCount } = propertyData;
        return propertyCount === 0 ? (
          propertyCount
        ) : (
          <TextButton onClick={() => previewProperties(user)}>{propertyCount}</TextButton>
        );
      }
      return <Spinner size="sm" />;
    };

    const unpaidPropertyRenderer = (user: UserManagementUserProfileDto) => {
      if (user.isPendingDelete) {
        return '-';
      }

      const onlyUnpaidProperties = true;
      const userId = user.isPending ? user.email : user.userToken;
      const propertyData = companyUsers.propertyData[userId];
      if (!propertyData) {
        return <Spinner size="sm" />;
      }
      if (propertyData.loadingState === LoadingConstants.ERROR) {
        return '-';
      }
      if (propertyData.loadingState === LoadingConstants.SUCCESS) {
        const { olympusTier3ListingCount } = propertyData;
        return olympusTier3ListingCount === 0 ? (
          olympusTier3ListingCount
        ) : (
          <TextButton onClick={() => previewProperties(user, onlyUnpaidProperties)}>
            {olympusTier3ListingCount}
          </TextButton>
        );
      }
      return <Spinner size="sm" />;
    };

    if (isScreenMobile) {
      return [
        {
          width: 10,
          header: 'User',
          isSortable: true,
          align: 'left',
          renderer: userRenderer,
        },
        {
          width: 2,
          header: 'Permissions',
          align: 'left',
          renderer: actionRenderer,
        },
      ];
    }
    const desktopColumnConfig: Array<ColumnConfig<UserManagementUserProfileDto>> = [
      {
        width: 3,
        header: 'User',
        isSortable: true,
        align: 'left',
        renderer: userRenderer,
      },
      {
        width: 3,
        header: 'Role',
        align: 'left',
        renderer: roleRenderer,
      },
    ];
    if (activeTab === 'users') {
      desktopColumnConfig.push({
        width: 2,
        header: hasOlympusListings ? 'Paid Properties' : 'Properties',
        align: 'left',
        renderer: paidPropertyRenderer,
      });
      if (hasOlympusListings) {
        desktopColumnConfig.push({
          width: 2,
          header: 'Bonus Properties',
          align: 'left',
          renderer: unpaidPropertyRenderer,
        });
      }
      desktopColumnConfig.push({
        width: 2,
        renderer: (data: UserManagementUserProfileDto) => {
          if (!data.lastActivityTimestamp) {
            return '-';
          }
          const date = new Date(parseFloat(data.lastActivityTimestamp));
          return date.toLocaleDateString();
        },
        dataKey: 'lastActivityTimestamp',
        header: 'Last Activity',
        align: 'left',
      });
      desktopColumnConfig.push({
        width: 2,
        header: 'Permissions',
        align: 'left',
        renderer: actionRenderer,
      });
    } else {
      desktopColumnConfig.push({
        width: 3,
        header: 'Additional properties',
        align: 'center',
        renderer: paidPropertyRenderer,
      });
      desktopColumnConfig.push({
        width: 3,
        header: 'Permissions',
        align: 'center',
        renderer: actionRenderer,
      });
    }
    return Object.values(desktopColumnConfig);
  },
};

export default columnConfigs;
